import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subscription, Subject } from 'rxjs';

import { CheckoutItemModel } from './models/item.model';
import { CheckoutBasketModel } from './models/basket.model';
import { CheckoutPaymentModel } from './models/payment.model';
import { ApiService } from '../api/api.service';
import { HttpHeaders } from '@angular/common/http';
import { AuthService } from '../auth/auth.service';

import { CONFIG as STRIPE_CONFIG } from '../config/stripe.config';
import { AppState } from '../app.service';
import { stripGeneratedFileSuffix } from '@angular/compiler/src/aot/util';
import { UserModel } from '../auth/models/user.model';
import { ReceiptDataModel } from './models/receiptData.model';
import { ButtonsService } from '../buttons/buttons.service';
import { COUNTRIES } from '../form/input/countries/countries.data';
import { RECEIPTTEMPLATE } from './receiptTemplate.data';
import { ReceiptWorthMappingService } from './receiptWorthMapping.service';
import { EmbeddedFonts } from '../global/embedded-fonts';

export interface CheckoutVoucherInterface {
  code: string;
  quantity: string;
  isValid: string;
  isGift: string;
  button: {
    design: string,
    value: string,
    spiritual: string,
    name: {
      firstName: string,
      lastName: string
    },
    presentee: {
      email: string,
      firstName: string,
      lastName: string
    }
  };
}

export interface CheckoutPaymentTypeInterface {
  type: 'paypal' | 'stripe' | 'none';
  paypal?: {
    paymentId: string,
    payerId: string
  };
  stripe?: {
    tokenId: string;
    country: string;
    paymentIntent?: string;
  };
}

@Injectable()
export class CheckoutService {
  private dataStore: {
    items: CheckoutItemModel[],
    user: UserModel,
    receiptData: ReceiptDataModel,
    payment: CheckoutPaymentModel
  } = {
      items: [],
      user: null,
      receiptData: null,
      payment: null
    };

  private _basket: CheckoutBasketModel = new CheckoutBasketModel();

  private _itemsChange: Subject<CheckoutItemModel[]> = new Subject();
  private _basketChange: Subject<CheckoutBasketModel> = new Subject();

  private _stripeInstance: stripe.Stripe;

  private _designValues: string[] = new CheckoutItemModel().availableDesigns;

  private _spiritualValues: { [s: string]: string } = {
    v1: 'Abenteuergeist',
    v2: 'besondere Augenblicke',
    v3: 'Bildung',
    v4: 'Dankbarkeit',
    v5: 'Farbe im Leben',
    v6: 'Freude',
    v7: 'Gerechtigkeit',
    v8: 'Glauben an das Gute',
    v9: 'große Träume',
    v10: 'Herzlichkeit',
    v11: 'Hoffnung',
    v12: 'Ja',
    v13: 'Kind in mir',
    v14: 'Leichtigkeit',
    v15: 'Liebe',
    v16: 'magische Schlüssel',
    v17: 'Musik im Leben',
    v18: 'Phantasie ',
    v19: 'Poesie im Leben',
    v20: 'Solidarität',
    v21: 'Vertrauen',
    v22: 'wahre Gefühle',
    v23: 'Wahrheit',
    v24: 'Zeit mit Kindern'
  };

  private subscriptionReceiptString: Subscription;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private appState: AppState,
    private buttonsService: ButtonsService,
    private receiptWorthMappingService: ReceiptWorthMappingService
  ) {
    this._stripeInstance = Stripe(STRIPE_CONFIG.publishableKey[this.appState.env]);
  }

  public get stripeInstance(): stripe.Stripe {
    return this._stripeInstance;
  }

  public get itemsChange(): Observable<CheckoutItemModel[]> {
    return this._itemsChange.asObservable();
  }

  public get basketChange(): Observable<CheckoutBasketModel> {
    return this._basketChange.asObservable();
  }

  public get basket(): CheckoutBasketModel {
    return this._basket;
  }

  public get designValues(): string[] {
    return this._designValues;
  }

  public get spiritualValues(): { [s: string]: string } {
    return this._spiritualValues;
  }

  public set items(items: CheckoutItemModel[]) {
    this.dataStore.items = items;

    this._itemsChange.next(this.dataStore.items);
  }

  public get items(): CheckoutItemModel[] {
    return this.dataStore.items;
  }

  public get payment(): CheckoutPaymentModel {
    return this.dataStore.payment;
  }

  public set payment(payment: CheckoutPaymentModel) {
    this.dataStore.payment = payment;
  }

  public get user(): UserModel {
    return this.dataStore.user;
  }

  public set user(user: UserModel) {
    this.dataStore.user = user;
  }

  public get receiptData(): ReceiptDataModel {
    return this.dataStore.receiptData;
  }

  public set receiptData(data: ReceiptDataModel) {
    this.dataStore.receiptData = data;
  }

  public itemFactoryFromStorage(data): CheckoutItemModel {
    const model = new CheckoutItemModel();

    model.price = data.price;
    model.spiritual = data.spiritual;
    model.voucher = data.voucher;
    model.design = data.design;
    model.isGift = data.isGift;
    model.gift = data.gift;

    return model;
  }

  public createStripeToken(cardNumberElement: stripe.elements.Element, name: string): Observable<stripe.Token> {
    const subject = new Subject<stripe.Token>();

    this._stripeInstance.createToken(cardNumberElement, {name})
      .then(
        (result) => {
          if (result.error) {
            subject.error(result.error);
          } else {
            subject.next(result.token);
          }
        }
      )
      .catch(
        (err) => {
          subject.error(err);
        }
      );

    return subject.asObservable();
  }

  public createStripePaymentMethod(cardNumberElement: stripe.elements.Element): Observable<string> {
    const subject = new Subject<string>();

    this._stripeInstance.createPaymentMethod({
        type: 'card',
        card: cardNumberElement
      })
      .then(
        (result) => {
          if (result.error) {
            subject.error(result.error);
          } else {
            subject.next(result.paymentMethod.id);
          }
        }
      )
      .catch(
        (err) => {
          subject.error(err);
        }
      );

    return subject.asObservable();
  }

  public handleStripeCardAction(paymentIntent: string): Observable<any> {
    const subject = new Subject<any>();

    this._stripeInstance.handleCardAction(paymentIntent)
      .then(
        (result) => {
          if (result.error) {
            subject.error(result.error);
          } else {
            subject.next(result);
          }
        }
      )
      .catch(
        (err) => {
          subject.error(err);
        }
      );

    return subject.asObservable();
  }

  public createReceiptPdfBaseString(template: object): Observable<string> {
    const subject = new Subject<string>();

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.currentUser.token
      })
    };

    this.subscriptionReceiptString = this.apiService.post('html-to-pdf', template, httpOptions)
      .subscribe(
        (result) => {
          subject.next(result.data);
        },
        (err) => {
          subject.error({
            key: 'api_error',
            original: err
          });
        }
      );

    return subject.asObservable();
  }

  public checkout(payment: CheckoutPaymentTypeInterface): Observable<any> {
    const subject = new Subject();

    console.log('checkout called');
    console.log('authorization token ' + (this.authService.currentUser.token ? 'present' : 'not present'));

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.currentUser.token
      })
    };

    const payload = {
      payment,
      buttons: [],
      receiptData: null
    };

    for (const item of this.dataStore.items) {
      const button: any = {
        value: item.price,
        spiritual: item.spiritual,
        design: item.design
      };

      if (item.voucher && item.voucher.code) {
        button.voucher = item.voucher.code;
      }

      if (item.gift && item.gift.firstName && item.gift.lastName && item.gift.email) {
        button.gift = {
          firstName: item.gift.firstName,
          lastName: item.gift.lastName,
          email: item.gift.email
        };

        if (item.gift.greetingsMessage) {
          button.gift.greetingsMessage = item.gift.greetingsMessage;
        }

        button.gift.giverFirstName = this.user.firstName;
        button.gift.giverLastName = this.user.lastName;
      }

      payload.buttons.push(button);
    }

    if (typeof this.dataStore.receiptData !== 'undefined' && this.dataStore.receiptData !== null) {
      let template = RECEIPTTEMPLATE;
      const receiptData = this.dataStore.receiptData;
      const country = COUNTRIES.filter((ctry) => ctry.i === receiptData.country)[0].n;
      const worthInWords = this.receiptWorthMappingService.mapWorthToWords(receiptData.worth);

      template = template.replace('%[customerName]%', `${receiptData.firstName} ${receiptData.lastName}, ${receiptData.street} ${receiptData.streetNumber}, ${receiptData.postalCode} ${receiptData.city}, ${country}`);
      template = template.replace('%[worth]%', `${receiptData.worth},-`);
      template = template.replace('%[worthInWords]%', `${worthInWords}`);
      template = template.replace('%[worthDate]%', `${receiptData.worthDate}`);
      template = template.replace('%[signatureDate]%', `${receiptData.worthDate}`);
      template = template.replace('%[fontFamilyNormal]%', `${EmbeddedFonts['Maison-Neue-Normal']}`);
      template = template.replace('%[fontFamilyBold]%', `${EmbeddedFonts['Maison-Neue-Bold']}`);

      if (worthInWords.length > 17) {
        const wordsWorthWidth = (5.133333333333333 / 22) * (worthInWords.length + 5);
        const noWordsWorthWidth = (15.399999999999999 - wordsWorthWidth) / 2;
        template = template.replace(/(%\[noWordsWorthWidth\]%)+/g, `${noWordsWorthWidth}cm`);
        template = template.replace(/(%\[wordsWorthWidth\]%)+/g, `${wordsWorthWidth}cm`);
      } else {
        template = template.replace(/(%\[noWordsWorthWidth\]%)+/g, '5.133333333333333cm');
        template = template.replace(/(%\[wordsWorthWidth\]%)+/g, '5.133333333333333cm');
      }

      const templateJson = {
        html: template
      };

      this.createReceiptPdfBaseString(templateJson)
        .subscribe((respData) => {
          payload.receiptData = respData;
          console.log('starting checkout');

          this.apiService.post('checkout/main-checkout', payload, httpOptions)
            .subscribe(
              (result) => {
                console.log('checkout main result');
                console.log(result);

                if (result.errorMessage) {
                  let error;

                  try {
                    error = JSON.parse(result.errorMessage);
                  } catch (e) {
                    error = {
                      key: 'unknown',
                      original: result.errorMessage
                    };
                  }

                  console.log('checkout failed', error);

                  subject.error(error);
                } else {
                  subject.next(result);
                }
              },
              (err) => {
                subject.error({
                  key: 'api_error',
                  original: err
                });
              }
            );
        });
    } else {
      console.log('starting checkout');

      this.apiService.post('checkout/main-checkout', payload, httpOptions)
        .subscribe(
          (result) => {
            console.log('checkout main result');
            console.log(result);

            if (result.errorMessage) {
              let error;

              try {
                error = JSON.parse(result.errorMessage);
              } catch (e) {
                error = {
                  key: 'unknown',
                  original: result.errorMessage
                };
              }

              console.log('checkout failed', error);

              subject.error(error);
            } else {
              subject.next(result);
            }
          },
          (err) => {
            subject.error({
              key: 'api_error',
              original: err
            });
          }
        );
    }

    return subject.asObservable();
  }

  public braintreeCheckout(): Observable<any> {
    const subject = new Subject();

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.authService.currentUser.token
      })
    };

    const payload = {
      nonce: this.dataStore.payment.nonce,
      buttons: []
    };

    for (const item of this.dataStore.items) {
      const button: any = {
        value: item.price,
        spiritual: item.spiritual,
        design: item.design
      };

      if (item.voucher && item.voucher.code) {
        button.voucher = item.voucher.code;
      }

      if (item.gift && item.gift.firstName && item.gift.lastName && item.gift.email) {
        button.gift = {
          firstName: item.gift.firstName,
          lastName: item.gift.lastName,
          email: item.gift.email
        };
      }

      payload.buttons.push(button);
    }

    this.apiService.post('braintree/checkout', payload, httpOptions)
      .subscribe(
        (result) => {
          if (result.errorMessage) {
            console.log('payment error',
              result.errorMessage, result.errorMessage.indexOf('payment_method_nonce does not contain'));

            if (result.errorMessage === 'Expiration date is invalid.') {
              console.log('Invalid expiration date');

              subject.error({
                code: 'InvalidExpirationDateException'
              });
            }
            if (result.errorMessage.indexOf('payment_method_nonce does not contain') >= 0) {
              console.log('Invalid card number');

              subject.error({
                code: 'InvalidCardNumberException'
              });
            }

            subject.error({
              code: 'UnknownErrorException'
            });
          } else {
            subject.next(result);
          }
        },
        (err) => {
          subject.error(err);
        }
      );

    return subject.asObservable();
  }

  public voucherCheck(code: string): Observable<CheckoutVoucherInterface> {
    return this.apiService.post('voucher/check-with-user', {
      voucher: code
    });
  }

  public calculateTotals(): CheckoutBasketModel {
    this._basket.calculate(this.dataStore.items);

    let itemsForBasket = [];

    this.dataStore.items.forEach(itemForBasket => {
      let itemObject = {
        price: itemForBasket.price,
        isGift: itemForBasket.isGift,
        voucher: itemForBasket.voucher,
        spiritual: this.buttonsService.mapSpiritual(itemForBasket.spiritual)
      };

      if (itemForBasket.isGift === true) {
        itemObject = Object.assign(itemObject, {
          gift: {
            firstName: itemForBasket.gift.firstName,
            lastName: itemForBasket.gift.lastName,
            email: itemForBasket.gift.email,
            greetingsMessage: itemForBasket.gift.greetingsMessage
          }
        });
      }

      if (typeof itemForBasket.voucher !== 'undefined' && typeof itemForBasket.isGift === 'undefined' ||
          typeof itemForBasket.voucher !== 'undefined' && itemForBasket.isGift === false) {
        if (typeof itemForBasket.voucher.button === 'undefined') {
          itemObject = Object.assign(itemObject, {
            isRealButton: true
          });
        } else {
          itemObject = Object.assign(itemObject, {
            isRealButton: false
          });
        }
      } else {
        itemObject = Object.assign(itemObject, {
          isRealButton: false
        });
      }

      itemsForBasket.push(itemObject);
    });

    this._basket.basketItems = itemsForBasket;
    this._basketChange.next(this._basket);

    return this._basket;
  }

  public addItem(item: CheckoutItemModel) {
    this.dataStore.items.push(item);

    this.calculateTotals();

    this._itemsChange.next(this.dataStore.items);
  }

  public updateItem(item: CheckoutItemModel, index: number) {
    this.dataStore.items[index] = item;

    this.calculateTotals();

    this._itemsChange.next(this.dataStore.items);
  }

  public removeItem(item: CheckoutItemModel, index: number) {
    this.dataStore.items.splice(index, 1);

    this.calculateTotals();

    this._itemsChange.next(this.dataStore.items);
  }

  public reset() {
    this.dataStore = {
      items: [],
      user: null,
      receiptData: null,
      payment: null
    };

    this._basket = new CheckoutBasketModel();

    this.calculateTotals();

    this._itemsChange.next(this.dataStore.items);
  }

  public init() {
    const storedItems = window.localStorage.getItem('checkoutItems');

    this.dataStore = {
      items: [],
      user: null,
      receiptData: null,
      payment: null
    };

    if (storedItems) {
      const items = JSON.parse(storedItems);

      for (const item of items) {
        this.dataStore.items.push(this.itemFactoryFromStorage(item));
      }

      this.calculateTotals();
    }

    this.itemsChange.subscribe(
      (items) => {
        if (items.length === 1) {
          if (items[0].isTouched()) {
            window.localStorage.setItem('checkoutItems', JSON.stringify(items));
          }
        } else if (items.length > 1) {
          window.localStorage.setItem('checkoutItems', JSON.stringify(items));
        }
      }
    );
  }

  public setUser(user: UserModel) {
    this.dataStore.user = user;
  }

  public setPayment(payment: CheckoutPaymentModel) {
    this.dataStore.payment = payment;
  }

  public NgOnDestroy() {
    if (typeof this.subscriptionReceiptString !== 'undefined') {
      this.subscriptionReceiptString.unsubscribe();
    }
  }
}


// WEBPACK FOOTER //
// ./src/app/checkout/checkout.service.ts